import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import ReviewCardField from 'platform/forms-system/src/js/components/ReviewCardField';
import get from 'platform/utilities/data/get';
import { errorSchemaIsValid } from 'platform/forms-system/src/js/validation';
import { scrollToFirstError } from 'platform/utilities/scroll';

const BANK_ACCOUNT_FIELDS = [
  'accountType',
  'routingNumber',
  'accountNumber',
  'bankName',
];

export const hasEnteredBankAccount = directDeposit =>
  BANK_ACCOUNT_FIELDS.some(field => !!directDeposit?.[field]);

export class DirectDepositField extends ReviewCardField {
  constructor(props) {
    super(props);

    // ReviewCardField wires its Save/Cancel buttons to `update`/`cancelUpdate`
    this.baseUpdate = this.update;
    this.baseCancelUpdate = this.cancelUpdate;
    this.update = this.handleSave;
    this.cancelUpdate = this.handleCancel;

    const { accountType, routingNumber, accountNumber } = props.formData ?? {};
    const emptyData =
      !accountType &&
      !routingNumber &&
      !accountNumber &&
      !props.hasPrefilledBankAccount;
    const startInEditConfigOption = get(
      'ui:options.startInEdit',
      this.props.uiSchema,
      false,
    );

    let shouldStartInEdit = startInEditConfigOption;
    if (typeof startInEditConfigOption === 'function') {
      shouldStartInEdit = startInEditConfigOption(this.props.formData);
    }

    const editing = emptyData || shouldStartInEdit;

    this.state = {
      editing,
      canCancel: !emptyData,
      oldData: props.formData,
    };
  }

  handleSave = event => {
    const { formData, hasPrefilledBankAccount, setShowRequiredErrors } =
      this.props;

    // The page schema allows an empty account when one is prefilled, so an
    // empty edit form has to be rejected here instead
    if (hasPrefilledBankAccount && !hasEnteredBankAccount(formData)) {
      event?.preventDefault();
      setShowRequiredErrors(true);
      this.showFieldErrors();
      return;
    }

    if (!errorSchemaIsValid(this.props.errorSchema)) {
      event?.preventDefault();
      this.showFieldErrors();
      return;
    }

    setShowRequiredErrors(false);
    this.baseUpdate(event);
    this.closeReviewEdit();
  };

  handleCancel = event => {
    this.baseCancelUpdate(event);
    // Cancel restores the data from when editing started. If that was an
    // incomplete account (e.g. left over from an earlier session), fall back to
    // the prefilled account so Cancel can always leave edit mode.
    const { oldData } = this.state;
    if (
      this.props.hasPrefilledBankAccount &&
      hasEnteredBankAccount(oldData) &&
      !BANK_ACCOUNT_FIELDS.every(field => oldData[field])
    ) {
      this.props.onChange({});
    }
    this.props.setShowRequiredErrors(false);
    this.clearFieldErrors();
    // Cancel must always leave edit mode, even if the restored data is incomplete
    const { formContext } = this.props;
    if (formContext.onReviewPage) {
      setTimeout(() => formContext.onCancelEdit?.(), 0);
    }
  };

  closeReviewEdit = () => {
    const { formContext } = this.props;
    if (formContext.onReviewPage) {
      setTimeout(() => formContext.onEdit?.(), 0);
    }
  };

  showFieldErrors = () => {
    this.props.formContext.setTouched(this.getFieldErrorState(true), () =>
      scrollToFirstError(),
    );
  };

  clearFieldErrors = () => {
    this.props.formContext.setTouched(this.getFieldErrorState(false));
  };

  getFieldErrorState = showErrors => {
    const { idSchema } = this.props;
    return BANK_ACCOUNT_FIELDS.reduce(
      (fields, field) =>
        idSchema?.[field]
          ? { ...fields, [idSchema[field].$id]: showErrors }
          : fields,
      {},
    );
  };

  getReviewView = () => {
    if (this.props.formContext.onReviewPage) {
      return this.getEditView();
    }

    const { viewComponent: ViewComponent } = this.props.uiSchema['ui:options'];
    const bodyClasses = [
      'vads-u-border--1px',
      'vads-u-border-color--gray-medium',
      /* Remove the top border because it looks like it just extends the header */
      'vads-u-border-top--0',
      'vads-u-padding--1p5',
      'vads-u-margin-bottom--1',
    ].join(' ');

    return (
      <div className="review-card">
        <div className={bodyClasses}>
          <ViewComponent
            formData={this.props.formData}
            formContext={this.props.formContext}
            errorSchema={this.props.errorSchema}
            startEditing={this.startEditing}
          />
        </div>
      </div>
    );
  };
}

const withRequiredErrors = ({ errorSchema, uiSchema, formData }) => ({
  errorSchema: BANK_ACCOUNT_FIELDS.reduce(
    (errors, field) =>
      formData?.[field]
        ? errors
        : {
            ...errors,
            [field]: {
              ...errorSchema?.[field],
              __errors: [
                uiSchema?.[field]?.['ui:errorMessages']?.required ||
                  'You must provide a response',
              ],
            },
          },
    errorSchema ?? {},
  ),
});
export default function DirectDepositFieldContainer(props) {
  const prefilledBankAccount = useSelector(
    state => state.form?.data?.['view:prefilledBankAccount'],
  );
  const hasPrefilledBankAccount = !!prefilledBankAccount;
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);

  const requiredErrorProps =
    showRequiredErrors &&
    hasPrefilledBankAccount &&
    !hasEnteredBankAccount(props.formData)
      ? withRequiredErrors(props)
      : {};

  const schema = { ...props.schema, required: BANK_ACCOUNT_FIELDS };

  return (
    <DirectDepositField
      {...props}
      {...requiredErrorProps}
      schema={schema}
      hasPrefilledBankAccount={hasPrefilledBankAccount}
      setShowRequiredErrors={setShowRequiredErrors}
    />
  );
}

DirectDepositFieldContainer.propTypes = {
  formData: PropTypes.object,
  schema: PropTypes.object,
};
